import { useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { SPAWN_REASONS, type VehicleState } from '../model/vehicleDriving';

/** 안내를 띄워 두는 시간 (ms). 읽을 만큼은 되고, 다시 달리기 시작할 때는 사라져야 합니다. */
const NOTICE_DURATION_MS = 1800;

export interface DrivingRunoffNoticeProps {
  stateRef: RefObject<VehicleState>;
}

/**
 * 코스 이탈 안내.
 *
 * 왜 필요한가: 코스를 벗어나면 차가 한 프레임에 출발선으로 옮겨집니다. 노면에 이탈
 * 구간을 어둡게 칠해 뒀지만(DrivingGround), 그것만으로는 "왜 갑자기 처음으로 돌아갔지"에
 * 답하지 못합니다. 설명 없는 순간이동은 규칙이 아니라 버그로 읽힙니다.
 *
 * "출발선으로"를 눌러서 돌아온 경우에는 띄우지 않습니다. 사용자가 직접 시킨 일을 다시
 * 알려 주는 것은 소음입니다. 그래서 횟수만 세지 않고 사유(spawnReason)까지 봅니다.
 *
 * 상태를 rAF로 훑는 이유는 DrivingSpeedReadout과 같습니다 — 운동 상태는 3D 루프가 ref에
 * 쓰기 때문에 React가 알 방법이 없습니다. 여기서 바뀌는 것은 이 컴포넌트뿐이라
 * 리스폰마다 두 번(켜기·끄기) 다시 렌더됩니다.
 */
export function DrivingRunoffNotice({ stateRef }: DrivingRunoffNoticeProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let frame = 0;
    let seenSpawn = stateRef.current.spawnCount;
    let hideAt = 0;

    const tick = () => {
      const state = stateRef.current;
      if (state.spawnCount !== seenSpawn) {
        seenSpawn = state.spawnCount;
        if (state.spawnReason === SPAWN_REASONS.RUNOFF) {
          hideAt = performance.now() + NOTICE_DURATION_MS;
          setVisible(true);
        }
      }
      if (hideAt !== 0 && performance.now() >= hideAt) {
        hideAt = 0;
        setVisible(false);
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [stateRef]);

  /*
   * 컨테이너는 항상 렌더합니다. aria-live 영역은 **미리 있어야** 내용이 채워질 때
   * 읽어 줍니다. 조건부로 넣으면 요소가 나타나는 것과 동시에 내용이 들어와서
   * 스크린 리더가 변화를 놓칩니다.
   */
  return (
    <div className="driving-runoff-notice" role="status" aria-live="polite">
      {visible ? (
        <p className="driving-runoff-notice__message">코스를 벗어나 출발선에서 다시 시작합니다</p>
      ) : null}
    </div>
  );
}
